import type { UIMessage } from 'ai';

/**
 * Reconcile a still-live message list with the server-authoritative persisted
 * transcript, deduping by STABLE message id (UIMessage.id === conversation_messages.id,
 * stable since the stable-ids milestone). Used after a WS reconnect mid-run
 * (design §8.5): the live list may be missing frames dropped on the closed
 * socket, or hold a partial assistant message that the server has since finalized.
 *
 * Rules:
 *  - Output order = persisted order, then any live-only ids appended in live order.
 *  - On id conflict, the PERSISTED message wins (nexus is the transcript-of-record,
 *    persist-before-send guarantees it is at least as complete as the live copy).
 *  - No id appears twice (no duplication); no persisted id is dropped (no gap).
 *  - Duplicate ids WITHIN a single input array are also collapsed: the first
 *    occurrence wins within that array (a persisted row that appears twice, or a
 *    live array that double-lists an optimistic message, must not leak a dup).
 */
export function reconcileMessages(
  live: ReadonlyArray<UIMessage>,
  persisted: ReadonlyArray<UIMessage>,
): UIMessage[] {
  const out: UIMessage[] = [];
  const seen = new Set<string>();
  // Persisted first (server-authoritative; wins on cross-array conflict), then
  // live-only ids in live order. The shared `seen` set dedups both across the
  // arrays and within each one (first occurrence wins).
  for (const m of persisted) {
    if (seen.has(m.id)) continue;
    seen.add(m.id);
    out.push(m);
  }
  for (const m of live) {
    if (seen.has(m.id)) continue;
    seen.add(m.id);
    out.push(m);
  }
  return out;
}
